import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const AuditTrail = ({ auditLogs, onExportLogs }) => {
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [dateRange, setDateRange] = useState('7days');

  const filters = [
    { id: 'all', label: 'All Activities', count: auditLogs?.length },
    { id: 'document', label: 'Documents', count: auditLogs?.filter(log => log?.category === 'document')?.length },
    { id: 'signature', label: 'Signatures', count: auditLogs?.filter(log => log?.category === 'signature')?.length },
    { id: 'communication', label: 'Communications', count: auditLogs?.filter(log => log?.category === 'communication')?.length },
    { id: 'system', label: 'System', count: auditLogs?.filter(log => log?.category === 'system')?.length }
  ];

  const dateRanges = [
    { id: '24hours', label: 'Last 24 Hours' },
    { id: '7days', label: 'Last 7 Days' },
    { id: '30days', label: 'Last 30 Days' },
    { id: 'all', label: 'All Time' }
  ];

  const filteredLogs = auditLogs?.filter(log => {
    const matchesFilter = selectedFilter === 'all' || log?.category === selectedFilter;
    
    if (dateRange === 'all') return matchesFilter;
    
    const logDate = new Date(log.timestamp);
    const now = new Date();
    const diffInHours = (now - logDate) / (1000 * 60 * 60);
    
    switch (dateRange) {
      case '24hours': return matchesFilter && diffInHours <= 24;
      case '7days': return matchesFilter && diffInHours <= 168;
      case '30days': return matchesFilter && diffInHours <= 720;
      default: return matchesFilter;
    }
  });

  const getActivityIcon = (action) => {
    switch (action) {
      case 'upload': return 'Upload';
      case 'download': return 'Download';
      case 'sign': return 'PenTool';
      case 'approve': return 'CheckCircle';
      case 'reject': return 'XCircle';
      case 'comment': return 'MessageCircle';
      case 'invite': return 'UserPlus';
      case 'login': return 'LogIn';
      case 'logout': return 'LogOut';
      case 'update': return 'Edit';
      case 'delete': return 'Trash2';
      case 'view': return 'Eye';
      case 'blockchain': return 'Shield';
      default: return 'Activity';
    }
  };

  const getActivityColor = (action) => {
    switch (action) {
      case 'upload': case 'approve': case 'sign': return 'text-success';
      case 'reject': case 'delete': return 'text-error';
      case 'update': case 'comment': return 'text-warning';
      case 'blockchain': return 'text-accent';
      default: return 'text-muted-foreground';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    
    return date?.toLocaleDateString() + ' ' + date?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Audit Trail</h3>
        <Button 
          variant="outline" 
          iconName="Download" 
          iconPosition="left"
          onClick={onExportLogs}
        >
          Export Logs
        </Button>
      </div>
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex space-x-2 overflow-x-auto">
          {filters?.map((filter) => (
            <button
              key={filter?.id}
              onClick={() => setSelectedFilter(filter?.id)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-smooth ${
                selectedFilter === filter?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              <span>{filter?.label}</span>
              <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                selectedFilter === filter?.id
                  ? 'bg-primary-foreground/20 text-primary-foreground'
                  : 'bg-background text-muted-foreground'
              }`}>
                {filter?.count}
              </span>
            </button>
          ))}
        </div>

        <select
          value={dateRange}
          onChange={(e) => setDateRange(e?.target?.value)}
          className="px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
        >
          {dateRanges?.map((range) => (
            <option key={range?.id} value={range?.id}>{range?.label}</option>
          ))}
        </select>
      </div>
      {/* Audit Log List */}
      <div className="bg-card border border-border rounded-lg">
        <div className="max-h-96 overflow-y-auto">
          {filteredLogs?.map((log, index) => (
            <div key={log?.id} className={`p-4 ${index !== filteredLogs?.length - 1 ? 'border-b border-border' : ''}`}>
              <div className="flex items-start space-x-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-muted ${getActivityColor(log?.action)}`}>
                  <Icon name={getActivityIcon(log?.action)} size={16} />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-sm font-medium text-foreground">{log?.description}</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <div className="flex items-center space-x-1">
                          <div className="w-4 h-4 rounded-full overflow-hidden bg-muted">
                            <Image 
                              src={log?.userAvatar} 
                              alt={log?.userName}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <span className="text-xs text-muted-foreground">{log?.userName}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">•</span>
                        <span className="text-xs text-muted-foreground">{formatTimestamp(log?.timestamp)}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      {log?.blockchainHash && (
                        <div className="flex items-center space-x-1 px-2 py-1 bg-success/10 rounded text-xs">
                          <Icon name="Shield" size={12} className="text-success" />
                          <span className="text-success">Verified</span>
                        </div>
                      )}
                      
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        log?.category === 'document' ? 'bg-accent/10 text-accent' :
                        log?.category === 'signature' ? 'bg-success/10 text-success' :
                        log?.category === 'communication'? 'bg-warning/10 text-warning' : 'bg-muted text-muted-foreground'
                      }`}>
                        {log?.category}
                      </span>
                    </div>
                  </div>
                  
                  {log?.details && (
                    <div className="mt-2 p-2 bg-muted/30 rounded text-xs text-muted-foreground">
                      {log?.details}
                    </div>
                  )}
                  
                  {log?.metadata && (
                    <div className="mt-2 flex items-center space-x-4 text-xs text-muted-foreground">
                      {log?.metadata?.ipAddress && (
                        <span>IP: {log?.metadata?.ipAddress}</span>
                      )}
                      {log?.metadata?.userAgent && (
                        <>
                          <span>•</span>
                          <span>{log?.metadata?.userAgent}</span>
                        </>
                      )}
                      {log?.blockchainHash && (
                        <>
                          <span>•</span>
                          <span className="font-mono">Hash: {log?.blockchainHash?.slice(0, 16)}...</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {filteredLogs?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="FileSearch" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No audit logs found</h3>
          <p className="text-muted-foreground">
            Try adjusting your filters to see more activities
          </p>
        </div>
      )}
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-accent">
            {filteredLogs?.filter(log => log?.category === 'document')?.length}
          </p>
          <p className="text-sm text-muted-foreground">Document Actions</p>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-success">
            {filteredLogs?.filter(log => log?.category === 'signature')?.length}
          </p>
          <p className="text-sm text-muted-foreground">Signatures</p>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-warning">
            {filteredLogs?.filter(log => log?.category === 'communication')?.length}
          </p>
          <p className="text-sm text-muted-foreground">Communications</p>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-foreground">
            {filteredLogs?.filter(log => log?.blockchainHash)?.length}
          </p>
          <p className="text-sm text-muted-foreground">Blockchain Verified</p>
        </div>
      </div>
    </div>
  );
};

export default AuditTrail;